/**
 * Small shared UI pieces for the meals module. Design tokens only (src/styles.css):
 * warm surfaces, quiet borders, 44px touch targets, visible focus rings.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { addDays, isThisWeek, mondayOf, todayIso, weekLabel } from './dates';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

export const btnPrimary = `inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-sage px-5 text-sm font-medium text-white transition-colors hover:bg-sage/90 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`;

export const btnSecondary = `inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`;

export const btnGhost = `inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full px-3 text-sm text-ink-soft transition-colors hover:bg-surface-sunk hover:text-ink disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`;

export const btnDanger = `inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-clay/30 bg-clay-soft px-4 text-sm font-medium text-clay transition-colors hover:bg-clay/15 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`;

/* text-base keeps iOS from zooming the viewport on focus */
export const inputCls = `min-h-[44px] w-full rounded-xl border border-border bg-surface px-3 py-2 text-base text-ink placeholder:text-ink-faint focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/30`;

export const labelCls = 'mb-1 block text-sm font-medium text-ink-soft';

export const cardCls = 'rounded-card border border-border bg-surface';

// ---------------------------------------------------------------- states

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="space-y-3" role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      {[0, 1, 2].map((i) => (
        <div key={i} className={`${cardCls} animate-pulse p-4`} aria-hidden="true">
          <div className="h-4 w-1/3 rounded bg-surface-sunk" />
          <div className="mt-3 h-10 rounded bg-surface-sunk" />
        </div>
      ))}
    </div>
  );
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-card border border-clay/30 bg-clay-soft p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm text-clay">
        <span aria-hidden="true">⚠ </span>
        {message}
      </p>
      {onRetry && (
        <button type="button" onClick={onRetry} className={btnSecondary}>
          Try again
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-card border border-dashed border-border bg-surface/60 px-6 py-12 text-center">
      <h3 className="font-display text-lg font-semibold text-ink">{title}</h3>
      <p className="mx-auto mt-1.5 max-w-sm text-sm text-ink-soft">{body}</p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- modal

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the panel, not the first field — auto-popping the phone keyboard is rude.
    panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-card bg-surface shadow-2xl outline-none sm:rounded-card"
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="font-display text-lg font-semibold text-ink">{title}</h2>
          <button type="button" onClick={onClose} className={btnGhost} aria-label="Close">
            <span aria-hidden="true">✕</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer && <div className="border-t border-border px-4 py-3">{footer}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- week nav

export function WeekNav({ week, onWeek }: { week: string; onWeek: (w: string) => void }) {
  const current = isThisWeek(week);
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className={btnGhost}
        onClick={() => onWeek(addDays(week, -7))}
        aria-label="Previous week"
      >
        <span aria-hidden="true">←</span>
      </button>

      <div className="flex-1 text-center">
        <p className="font-display text-base font-semibold text-ink">{weekLabel(week)}</p>
        <p className="text-xs text-ink-faint">{current ? 'This week' : 'Week of ' + week}</p>
      </div>

      <button
        type="button"
        className={btnGhost}
        onClick={() => onWeek(addDays(week, 7))}
        aria-label="Next week"
      >
        <span aria-hidden="true">→</span>
      </button>

      {!current && (
        <button type="button" className={btnSecondary} onClick={() => onWeek(mondayOf(todayIso()))}>
          Today
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- meal photo

/**
 * Meal photos live in a private R2 bucket and come back through the Worker.
 * With no photo we draw a warm tile with the meal's initial — never a broken box.
 */
export function MealPhoto({
  name,
  url,
  className = '',
}: {
  name: string;
  url: string | null;
  className?: string;
}) {
  if (url) {
    return (
      <img
        src={url}
        alt={`Photo of ${name}`}
        loading="lazy"
        className={`bg-surface-sunk object-cover ${className}`}
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      className={`flex items-center justify-center bg-gradient-to-br from-honey-soft to-clay-soft font-display text-2xl font-semibold text-clay ${className}`}
    >
      {name.trim().charAt(0).toUpperCase() || '·'}
    </div>
  );
}
